<script setup>
import {computed, onMounted, reactive, ref} from 'vue';
import axios from 'axios';

const state = reactive({
    data: null,
    isLoading: true,
    isCheckingOut: false,
    isConfirming: false,
    error: null,
    success: null,
    notice: null,
});

const orderedPlans = computed(() => {
    const pricing = state.data?.pricing ?? {};
    const preferredOrder = ['monthly', 'lifetime'];
    const plans = [];

    preferredOrder.forEach((key) => {
        if (pricing[key]) {
            plans.push(pricing[key]);
        }
    });

    Object.keys(pricing).forEach((key) => {
        if (! preferredOrder.includes(key)) {
            plans.push(pricing[key]);
        }
    });

    return plans;
});

const hasApp = computed(() => Boolean(state.data?.app_enabled));
const needsCard = computed(() => Boolean(state.data?.needs_payment_method));
const hasPro = computed(() => Boolean(state.data?.has_pro));
const canPurchase = computed(() => Boolean(state.data?.can_purchase) && ! needsCard.value && hasPro.value);
const termsAccepted = ref({monthly: false, lifetime: false});

function formatCurrency(amountCents, currency = 'eur') {
    if (amountCents === null || amountCents === undefined) {
        return '';
    }


    let lang = 'en-US';

    if (navigator.languages.length) {
        lang = navigator.languages[0]
    }

    return new Intl.NumberFormat(lang, {
        style: 'currency',
        currency: currency?.toUpperCase() ?? 'EUR',
        minimumFractionDigits: 0,
    }).format(amountCents / 100);
}

function planHeadline(plan) {
    if (plan.plan_type === 'monthly') {
        return 'Monthly';
    }
    if (plan.plan_type === 'lifetime') {
        return 'Lifetime';
    }

    return plan.name ?? 'Plan';
}

function publishedLaunchFeeAmount(plan) {
    return plan.launch_fee_amount_cents ?? 0;
}

function lifetimeTotal(plan) {
    if (plan.plan_type !== 'lifetime') {
        return plan.amount_cents;
    }

    return plan.amount_cents + publishedLaunchFeeAmount(plan);
}

function planCta(plan) {
    if (! hasPro.value) {
        return 'Upgrade to Pro required';
    }

    if (needsCard.value) {
        return 'Add a card first';
    }

    if (! state.data?.can_purchase) {
        return 'Already purchased';
    }

    if (plan.plan_type === 'lifetime') {
        return `Pay ${formatCurrency(plan.amount_cents, plan.currency)} today`;
    }

    return `Subscribe for ${formatCurrency(plan.amount_cents, plan.currency)}/month`;
}

function launchFeeCopy(plan) {
    if (!plan.launch_fee_amount_cents) {
        return null;
    }

    const amount = formatCurrency(plan.launch_fee_amount_cents, plan.currency);
    if (plan.plan_type === 'lifetime') {
        return `Reserve your build slot today for ${formatCurrency(plan.amount_cents, plan.currency)}.<br>The remaining ${formatCurrency(plan.amount_cents, plan.currency)} is charged only when your app goes live (${formatCurrency(plan.amount_cents * 2, plan.currency)} total).`;
    }
    return plan.launch_fee_description ?? `${amount} is billed when submitting to the App Store as you launch.`;
}

function formatStatus(order) {
    switch (order.status) {
        case 'completed':
            return 'Completed';
        case 'pending':
            return 'Pending payment';
        case 'failed':
            return 'Failed';
        case 'canceled':
            return 'Canceled';
        default:
            return order.status;
    }
}

async function loadStatus() {
    state.isLoading = true;
    state.error = null;
    try {
        const { data } = await axios.get(route('company.whitelabel-app.status'));
        state.data = data;
    } catch (error) {
        console.error(error);
        state.error = error.response?.data?.error ?? 'Unable to load branded app details right now.';
    } finally {
        state.isLoading = false;
    }
}

async function startCheckout(planType) {
    if (state.isCheckingOut || ! canPurchase.value) {
        return;
    }

    state.isCheckingOut = true;
    state.error = null;

    try {
        const { data } = await axios.post(route('company.whitelabel-app.checkout'), {
            plan_type: planType,
        });

        if (data?.url) {
            window.location.href = data.url;
        }
    } catch (error) {
        console.error(error);
        state.error = error.response?.data?.error ?? 'Unable to start checkout. Please try again in a moment.';
    } finally {
        state.isCheckingOut = false;
    }
}

async function confirmSession(sessionId) {
    if (! sessionId) {
        return;
    }

    state.isConfirming = true;
    state.error = null;
    state.notice = null;

    try {
        const { data } = await axios.post(route('company.whitelabel-app.confirm'), {
            session_id: sessionId,
        });

        if (data?.status === 'activated') {
            state.success = 'Success! Your branded app workspace is now unlocked.';
        } else if (data?.status === 'already_completed') {
            state.notice = 'This checkout was already confirmed earlier.';
        }

        await loadStatus();
    } catch (error) {
        console.error(error);
        state.error = error.response?.data?.error ?? 'Unable to confirm payment yet. Refresh the page shortly.';
    } finally {
        state.isConfirming = false;
    }
}

function handleCheckoutResult() {
    const params = new URLSearchParams(window.location.search);
    const checkoutStatus = params.get('whitelabelCheckout');
    const sessionId = params.get('session_id');

    if (checkoutStatus === 'success' && sessionId) {
        confirmSession(sessionId);
    } else if (checkoutStatus === 'cancelled') {
        state.notice = 'Checkout was cancelled. You can start again whenever you are ready.';
    }

    if (checkoutStatus) {
        params.delete('whitelabelCheckout');
        params.delete('session_id');
        const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
        window.history.replaceState({}, document.title, newUrl);
    }
}

function openBillingPage() {
    if (state.data?.billing_page_url) {
        window.open(state.data.billing_page_url, '_self');
    }
}

onMounted(async () => {
    await loadStatus();
    handleCheckoutResult();
});
</script>

<template>
    <div class="w-full">
        <div class="max-w-5xl px-4 py-10 mx-auto">
            <div class="text-center">
                <p class="text-sm font-semibold tracking-wide text-myflowGreen-900 uppercase">Branded customer app</p>
                <h1 class="mt-2 text-3xl font-bold text-slate-900">Launch your own myflow-powered app</h1>
                <p class="mt-3 text-base text-slate-600 max-w-3xl mx-auto">
                    Give your customers a fully branded iOS and Android experience, complete with your content,
                    navigation, and notifications. Pay monthly or secure lifetime access with a one-time fee.
                </p>
            </div>

            <div v-if="state.error" class="p-4 mt-6 rounded-lg bg-rose-50 text-rose-700">
                {{ state.error }}
            </div>

            <div v-if="state.notice" class="p-4 mt-6 rounded-lg bg-amber-50 text-amber-800">
                {{ state.notice }}
            </div>

            <div v-if="state.success" class="p-4 mt-6 rounded-lg bg-emerald-50 text-emerald-800">
                {{ state.success }}
            </div>

            <div
                v-if="state.isLoading"
                class="flex items-center justify-center mt-10"
            >
                <span class="text-slate-500">Loading...</span>
            </div>

            <div v-else class="mt-10 space-y-8">
                <div
                    v-if="hasApp"
                    class="flex flex-col gap-4 rounded-2xl border border-emerald-100 bg-emerald-50 p-6 text-emerald-900"
                >
                    <div>
                        <p class="text-sm font-semibold uppercase tracking-wide">Activated</p>
                        <h2 class="text-2xl font-semibold mt-1">Your branded app is unlocked</h2>
                        <p class="mt-2 text-emerald-800">
                            Manage navigation, upload store assets, and send push notifications under
                            <a :href="route('pages.index', 'app_page')"><span class="font-semibold">My company → Whitelabel App</span></a>.
                        </p>
                    </div>
                </div>

                <div
                    v-if="!hasPro"
                    class="flex flex-col gap-4 rounded-2xl border border-amber-200 bg-amber-50 p-6 text-amber-900"
                >
                    <div>
                        <h3 class="text-lg font-semibold">Upgrade to Pro required</h3>
                        <p class="mt-2 text-amber-800">
                            You need to upgrade to Pro before you can purchase the branded app. Upgrade your plan on the Plans & Pricing page, then return here to place the order.
                        </p>
                    </div>
                    <div>
                        <button
                            type="button"
                            class="inline-flex items-center justify-center rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
                            @click="openBillingPage"
                        >
                            Go to Plans & Pricing
                        </button>
                    </div>
                </div>

                <div
                    v-if="needsCard && hasPro"
                    class="flex flex-col gap-4 rounded-2xl border border-amber-200 bg-amber-50 p-6 text-amber-900"
                >
                    <div>
                        <h3 class="text-lg font-semibold">Add a card to continue</h3>
                        <p class="mt-2 text-amber-800">
                            Update your billing details on the Plans & Pricing page first, then return here to place the order.
                        </p>
                    </div>
                    <div>
                        <button
                            type="button"
                            class="inline-flex items-center justify-center rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
                            @click="openBillingPage"
                        >
                            Go to Plans & Pricing
                        </button>
                    </div>
                </div>

                <div class="grid gap-6 md:grid-cols-2">
                    <article
                        v-for="plan in orderedPlans"
                        :key="plan.plan_type"
                        class="flex flex-col rounded-2xl border border-slate-200 bg-white shadow-sm"
                        :class="plan.plan_type === 'lifetime' ? 'md:translate-y-2' : ''"
                    >
                        <div class="p-6 border-b border-slate-100">
                            <p class="text-sm font-semibold text-myflowGreen-900 uppercase tracking-wide">
                                {{ planHeadline(plan) }}
                            </p>
                            <h3 class="mt-2 text-2xl font-semibold text-slate-900">
                                <template v-if="plan.plan_type === 'monthly'">
                                    <p
                                        v-if="plan.original_amount_cents"
                                        class="flex items-center gap-2 text-sm text-slate-500 font-normal mb-0"
                                    >
                                        <span>Usually</span>
                                        <span class="line-through">
                                            {{ formatCurrency(plan.original_amount_cents) }}
                                        </span>
                                        <span
                                            class="uppercase text-xs bg-myflowGreen-100 text-myflowGreen-900 px-2 py-0.5 rounded-full"
                                        >
                                            Save {{ formatCurrency(plan.original_amount_cents - plan.amount_cents) }}
                                        </span>
                                    </p>


                                    {{ `${formatCurrency(plan.amount_cents, plan.currency)} / month` }}
                                </template>
                                <template v-else>
                                    <p
                                        v-if="plan.original_amount_cents"
                                        class="flex items-center gap-2 text-sm text-slate-500 font-normal mb-0"
                                    >
                                        <span>Usually</span>
                                        <span class="line-through">
                                            {{ formatCurrency(plan.original_amount_cents) }}
                                        </span>
                                        <span
                                            class="uppercase text-xs bg-myflowGreen-100 text-myflowGreen-900 px-2 py-0.5 rounded-full"
                                        >
                                            Save {{ formatCurrency(plan.original_amount_cents - (plan.amount_cents * 2)) }}
                                        </span>
                                    </p>

                                    {{ `${formatCurrency(lifetimeTotal(plan), plan.currency)} lifetime` }}
                                </template>
                            </h3>
                            <p v-if="plan.description" class="mt-2 text-sm text-slate-600">
                                {{ plan.description }}
                            </p>
                            <p
                                v-if="launchFeeCopy(plan)"
                                class="mt-1 text-xs text-slate-500"
                                v-html="launchFeeCopy(plan)"
                            >
                            </p>
                        </div>
                        <div class="flex-1 px-6 py-5 text-sm text-slate-600 space-y-2">
                            <p v-if="plan.plan_type === 'monthly'">
                                • Rolling subscription that covers hosting, updates, and support for your branded app.
                            </p>
                            <p v-else>
                                • Lifetime license for your branded app. Hosting and updates included forever.
                            </p>
                            <p>• Access to the Backoffice suite for managing content, navigation, and notifications.</p>
                            <p>• Dedicated support from the myflow onboarding team.</p>
                        </div>
                        <div class="px-6 pb-6">
                            <div
                                v-if="canPurchase"
                                class="flex justify-start w-auto space-x-2 items-center my-2"
                            >
                                <input type="checkbox"
                                   :id="`terms-${plan.plan_type}`"
                                    v-model="termsAccepted[plan.plan_type]"
                                />
                                <label :for="`terms-${plan.plan_type}`" class="text-xs text-slate-600">
                                    I accept the <a href="https://app.myflow.se/terms-whitelabel-app" target="_blank" class="mf-link">branded app terms</a>
                                </label>
                            </div>

                            <button
                                type="button"
                                class="inline-flex w-full items-center justify-center rounded-lg px-5 py-3 text-sm font-semibold transition"
                                :class="(! termsAccepted[plan.plan_type] || !canPurchase || state.isCheckingOut || !hasPro)
                                    ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                                    : 'bg-myflowGreen-900 text-white hover:bg-myflowGreen-800'"
                                :disabled="! termsAccepted[plan.plan_type] || !canPurchase || state.isCheckingOut || !hasPro"
                                @click="startCheckout(plan.plan_type)"
                            >
                                <span v-if="state.isCheckingOut">Redirecting…</span>
                                <span v-else>{{ planCta(plan) }}</span>
                            </button>
                            <p
                                v-if="plan.plan_type === 'lifetime'"
                                class="mt-2 text-xs text-slate-500 text-center"
                            >
                                {{ formatCurrency(plan.amount_cents, plan.currency) }} today + {{ formatCurrency(plan.launch_fee_amount_cents, plan.currency) }} at launch ({{ formatCurrency(lifetimeTotal(plan), plan.currency) }} total)
                            </p>
                            <p
                                v-if="!state.data?.can_purchase && hasApp"
                                class="mt-2 text-xs text-slate-500 text-center"
                            >
                                Already purchased
                            </p>
                        </div>
                    </article>
                </div>

                <div v-if="state.data?.orders?.length" class="p-6 rounded-2xl border border-slate-200 bg-white">
                    <h3 class="text-lg font-semibold text-slate-900">Recent branded app purchases</h3>
                    <ul class="mt-4 space-y-4 text-sm text-slate-600">
                        <li
                            v-for="order in state.data.orders"
                            :key="order.id"
                            class="flex items-start justify-between gap-4 border-b border-slate-100 pb-4 last:border-b-0 last:pb-0"
                        >
                            <div>
                                <p class="font-medium text-slate-900 capitalize">{{ order.plan_type }} plan</p>
                                <p class="text-xs text-slate-500">
                                    {{ formatStatus(order) }}
                                    <span v-if="order.purchased_at"> · {{ order.purchased_at }}</span>
                                </p>
                                <p v-if="order.launch_fee_pending" class="mt-1 text-xs text-amber-600">
                                    Launch fee pending – myflow will charge €995 when your app goes live.
                                </p>
                            </div>
                            <div class="text-right text-slate-900 font-semibold">
                                {{ formatCurrency(order.amount_cents, order.currency) }}
                            </div>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </div>
</template>
